import moment from 'moment'

import { next, range } from 'modules/core/lib/frequencies'
import i18n from 'modules/core/lib/i18n'

/**
 * Calculates the current and next period dates based on the payroll compensation cycle
 * @param cycleRecurrence - The compensation cycle recurrence from payroll (e.g., 'monthly')
 * @param currentDate - The current date to base calculations on
 * @returns Object with current and next period information
 */
export function getCompensationPeriods(cycleRecurrence: string, currentDate: Date = new Date()) {
  const momentDate = moment(currentDate)

  const [currentPeriodStart, currentPeriodEnd] = range(momentDate, cycleRecurrence, false)
  const nextPeriodStart = next(momentDate, cycleRecurrence)
  const [nextPeriodStartRange, nextPeriodEndRange] = range(nextPeriodStart, cycleRecurrence, false)

  return {
    current: {
      start: currentPeriodStart,
      end: currentPeriodEnd,
      label: formatPeriodLabel(cycleRecurrence, currentPeriodStart),
    },
    next: {
      start: nextPeriodStartRange,
      end: nextPeriodEndRange,
      label: formatPeriodLabel(cycleRecurrence, nextPeriodStartRange),
    },
  }
}

/**
 * Formats a period label based on the cycle type
 * @param cycleRecurrence - The compensation cycle recurrence
 * @param periodStart - The start date of the period
 * @returns Formatted period label
 */
function formatPeriodLabel(cycleRecurrence: string, periodStart: moment.Moment): string {
  const [from, to] = range(periodStart.clone(), cycleRecurrence, false)

  switch (cycleRecurrence) {
    case 'daily':
      return from.format('MMMM D, YYYY')
    case 'weekly':
    case 'fortnightly':
    case 'semimonthly':
      return `${from.format('MMM D')} - ${to.format('MMM D, YYYY')}`
    case 'monthly':
      return from.format('MMMM YYYY')
    default:
      return from.format('MMMM YYYY')
  }
}

/**
 * Gets the description text for a compensation period
 * @param isCurrent - Whether this is the current period
 * @returns Description text
 */
export function getCompensationPeriodDescription(isCurrent: boolean): string {
  return isCurrent
    ? i18n.t('time_tracking.settings.compensation.periods.current.description')
    : i18n.t('time_tracking.settings.compensation.periods.next.description')
}
